import React from 'react'
import { FeedItem } from '@/types/feedItems'
import { Globe, Video, MessageSquare, Sparkle, Binoculars, Lightbulb, TrendingUp, Cpu, ArrowRight } from 'lucide-react'
import { format } from 'date-fns'
import { Badge } from '@/components/ui/badge'
import { useFeedInteractions } from '@/hooks/useFeedInteractions'
import FeedInteractions from './FeedInteractions'
import { VideoPlayer } from './VideoPlayer'
import clsx from 'clsx'
import ReactMarkdown from 'react-markdown'
import { getDomain } from '@/utils'
import thumbnail from '@/assets/audio-thumbnail.png'

interface BaseFeedItemProps {
  item: FeedItem
}

const BaseFeedItem = ({ item }: BaseFeedItemProps) => {
  const { reactions, comments, handleReactionAdd, handleCommentAdd } = useFeedInteractions(
    (item as any).reactions || [],
    (item as any).comments || []
  )
  const [isExpanded, setIsExpanded] = React.useState(false)
  // Controls the hover state for the cluster indicator shown on Need / New-Insight cards
  const [showPercentage, setShowPercentage] = React.useState(false)

  // Icon mapping for different feed item types
  const getIcon = () => {
    switch (item.type) {
      case 'meeting':
        return <Video size={16} className="text-pink-700" />
      case 'chat':
        return <MessageSquare size={16} className="text-purple-700" />
      case 'insight':
        return <Lightbulb size={16} className="text-yellow-700" />
      case 'competitor':
        return <Binoculars size={16} className="text-blue-700" />
      case 'tech':
        return <Cpu size={16} className="text-purple-700" />
      case 'market':
        return <TrendingUp size={16} className="text-green-700" />
      case 'opportunity':
        return <Sparkle size={16} className="text-purple-700" />
      default:
        return <Globe size={16} className="text-gray-700 dark:text-gray-300" />
    }
  }

  // Format the date
  const getFormattedDate = () => {
    const dateStr = (item as any).date || ''
    if (dateStr.toLowerCase().includes('today')) {
      return dateStr
    }
    if (dateStr.toLowerCase().includes('yesterday')) {
      return dateStr
    }
    try {
      return format(new Date(dateStr), 'MMM d, yyyy')
    } catch {
      return dateStr
    }
  }

  function getFirstParagraph(text: string): string {
    // If the text contains double newlines, treat them as paragraph breaks
    const paragraphSplit = text.split(/\n\s*\n/)
    if (paragraphSplit.length > 1) {
      return paragraphSplit[0].trim()
    }

    // Otherwise, try to split on the first major sentence boundary
    const match = text.match(/(.+?[.?!])(?=\s+[A-Z])/)
    return match ? match[1].trim() : text.trim() // fallback to full text if no match
  }

  // Render content based on item type
  const renderContent = () => {
    switch (item.type) {
      case 'meeting':
        return (
          <div className="mt-3">
            {/* Add Video Player */}
            {item.videoUrl && (
              <div className="mb-6">
                <VideoPlayer recordingUrl={item.videoUrl} thumbnailUrl={item.thumbnailUrl || thumbnail} />
              </div>
            )}

            {/* Always try to render structured meeting content */}
            <div className="space-y-6">
              {/* Background Info - show if any background info exists */}
              {item.executive_summary &&
                [
                  item.executive_summary.interviewee_bg1,
                  item.executive_summary.interviewee_bg2,
                  item.executive_summary.interviewee_bg3,
                ].some((bg) => bg) && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
                    {[
                      item.executive_summary.interviewee_bg1,
                      item.executive_summary.interviewee_bg2,
                      item.executive_summary.interviewee_bg3,
                    ]
                      .filter(Boolean)
                      .map((bg, index) => (
                        <div key={index} className="flex items-center gap-2 bg-[#f9f9f9] rounded-[30px] py-1.5 px-4">
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7 13.125C10.3827 13.125 13.125 10.3827 13.125 7C13.125 3.61726 10.3827 0.875 7 0.875C3.61726 0.875 0.875 3.61726 0.875 7C0.875 10.3827 3.61726 13.125 7 13.125Z"
                              stroke="#62708D"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M7 4.8125V7"
                              stroke="#62708D"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M7 9.1875H7.00875"
                              stroke="#62708D"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <p className="text-[#62708d] text-sm font-extrabold font-['Manrope'] uppercase leading-[13.20px] tracking-wide">
                            {bg}
                          </p>
                        </div>
                      ))}
                  </div>
                )}

              {/* Takeaways - show if any exist */}
              {item.executive_summary &&
                Object.entries(item.executive_summary).filter(([key, value]) => key.startsWith('takeaway_') && value)
                  .length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Key Takeaways:</h4>
                    <ul className="space-y-4">
                      {Object.entries(item.executive_summary)
                        .filter(([key, value]) => key.startsWith('takeaway_') && value)
                        .map(([key, value], index) => (
                          <li key={index} className="flex items-start gap-3">
                            <svg
                              className="mt-1.5 flex-shrink-0"
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M7 13.125C10.3827 13.125 13.125 10.3827 13.125 7C13.125 3.61726 10.3827 0.875 7 0.875C3.61726 0.875 0.875 3.61726 0.875 7C0.875 10.3827 3.61726 13.125 7 13.125Z"
                                fill="#E5F5FF"
                              />
                              <path
                                d="M4.375 7L6.125 8.75L9.625 5.25"
                                stroke="#62708D"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <p className="text-lg text-gray-600 leading-relaxed">{value}</p>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

              {/* Validation Section - show if validation exists */}
              {item.validation?.is_prospect_to_use_it && (
                <div className="mt-6 p-6 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-medium text-gray-900">Prospect Status:</span>
                    <span
                      className={`text-lg ${
                        item.validation.is_prospect_to_use_it === 'yes'
                          ? 'text-green-600'
                          : item.validation.is_prospect_to_use_it === 'no'
                            ? 'text-red-600'
                            : 'text-yellow-600'
                      }`}
                    >
                      {item.validation.is_prospect_to_use_it.charAt(0).toUpperCase() +
                        item.validation.is_prospect_to_use_it.slice(1)}
                    </span>
                  </div>
                </div>
              )}

              {/* Fallback content if no structured data is available */}
              {(!item.executive_summary ||
                Object.keys(item.executive_summary).length === 0 ||
                (!item.executive_summary.interviewee_bg1 &&
                  !item.executive_summary.interviewee_bg2 &&
                  !item.executive_summary.interviewee_bg3 &&
                  Object.entries(item.executive_summary).filter(([key, value]) => key.startsWith('takeaway_') && value)
                    .length === 0)) &&
                !item.validation?.is_prospect_to_use_it && (
                  <>
                    <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">{item.summary}</p>
                    {item.keyPoints && item.keyPoints.length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Key Points:</h4>
                        <ul className="mt-4 list-disc list-inside space-y-3">
                          {item.keyPoints.map((point, index) => (
                            <li key={index} className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
            </div>
          </div>
        )

      case 'chat':
        return (
          <div className="mt-3">
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-6">{item.summary}</p>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-base text-gray-500">Customer:</span>
              <Badge variant="secondary" className="text-base px-3 py-1">
                {item.customer}
              </Badge>
            </div>
          </div>
        )

      case 'insight':
        return (
          <div className="mt-3">
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
              I detected a trending pattern in user feedback:
            </p>
            <div className="mt-6 relative flex items-center justify-center" style={{ minHeight: '320px' }}>
              <div className="absolute inset-0 bg-gray-100 rounded-lg" />
              <div className="absolute top-4 left-4">
                <Badge variant="secondary" className="text-xs uppercase tracking-wider bg-white">
                  Need
                </Badge>
              </div>
              <div
                className="relative w-[500px] h-[300px] py-10 px-8 rounded-lg shadow my-20"
                // Needs / New-Insight notes use the same yellow background as other Need notes (#FFC921)
                style={{ backgroundColor: '#FFC921' }}
              >
                <div className="h-full flex flex-col">
                  <div className="flex-1 flex items-center justify-center">
                    <p
                      className={clsx(
                        'text-slate-800 font-semibold hideScrollbar text-center overflow-hidden',
                        item.content.length < 120
                          ? 'text-3xl leading-relaxed tracking-wider'
                          : 'text-2xl leading-relaxed tracking-wide'
                      )}
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 5,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {item.content}
                    </p>
                  </div>
                  {/* Cluster indicator */}
                  {(item.meetings_distinct_count ?? 0) > 0 && (item.total_meetings_count ?? 0) > 0 && (
                    <div
                      className="absolute top-4 right-4"
                      onMouseEnter={() => setShowPercentage(true)}
                      onMouseLeave={() => setShowPercentage(false)}
                    >
                      <div
                        className={`relative transition-all duration-200 ease-in-out ${showPercentage ? 'w-[48px]' : 'w-[32px]'} h-[32px]`}
                      >
                        {/* Base circle plus progress stroke – hidden when showing percentage */}
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                          className={`absolute transition-opacity duration-200 ${showPercentage ? 'opacity-0' : 'opacity-100'}`}
                        >
                          <circle cx="16" cy="16" r="13" fill="black" stroke="#333333" strokeWidth="3" />
                          <circle
                            cx="16"
                            cy="16"
                            r="13"
                            fill="none"
                            stroke="#FF4A8E"
                            strokeWidth="4"
                            strokeLinecap="round"
                            strokeDasharray={`${(
                              (item.meetings_distinct_count / item.total_meetings_count) *
                              81.68
                            ).toFixed(2)} 81.68`}
                            transform="rotate(-90 16 16)"
                          />
                        </svg>
                        {/* Text value (number or percentage) */}
                        <div
                          className={`absolute inset-0 flex items-center justify-center ${showPercentage ? 'bg-black rounded-full px-2' : ''}`}
                        >
                          <span className="text-white text-sm font-bold font-['Manrope'] leading-none whitespace-nowrap">
                            {showPercentage
                              ? `${Math.round((item.meetings_distinct_count * 100) / item.total_meetings_count)}%`
                              : item.meetings_distinct_count}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )

      case 'competitor':
        return (
          <div className="mt-6">
            <h4 className="text-2xl font-semibold text-gray-900 mb-3">
              {item.news_title?.split('\n')[0].replace('# ', '')}
            </h4>
            <p className="text-lg text-gray-500 mb-4 leading-relaxed">{item.news_subtitle}</p>
            <div className="flex items-center gap-3 mb-6">
              {item.sources &&
                Array.isArray(item.sources) &&
                item.sources.map((source, index) => {
                  const domain = getDomain(source.url)
                  return (
                    <div key={index} className="flex items-center gap-2.5">
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2.5"
                      >
                        <img
                          src={`https://img.logo.dev/${domain}?token=${import.meta.env.VITE_LOGO_DEV_TOKEN}`}
                          alt={domain}
                          className="w-7 h-7 rounded-full object-cover"
                        />
                        <span className="text-base text-gray-500">{domain}</span>
                      </a>
                      {index < item.sources.length - 1 && <span className="text-base text-gray-300">•</span>}
                    </div>
                  )
                })}
            </div>
            {(item.primary_video_url || item.primary_image_url) && (
              <div className="mt-8 relative flex items-center justify-center" style={{ minHeight: '320px' }}>
                <div className="absolute inset-0 bg-gray-100 rounded-lg" />
                <div className="relative w-[600px] py-14 px-10">
                  <img
                    src={item.primary_video_url || item.primary_image_url}
                    alt={item.news_title}
                    className="w-full h-auto rounded-lg shadow-sm"
                  />
                </div>
              </div>
            )}
            <p className="text-lg text-gray-600 mt-8 leading-relaxed">{getFirstParagraph(item.fullText)}</p>
            {item.fullText && (
              <div className="mt-6">
                {isExpanded ? (
                  <div className="prose prose-lg max-w-none">
                    <ReactMarkdown
                      components={{
                        h1: ({ node, ...props }) => (
                          <h1 className="text-3xl font-semibold mt-10 mb-6 text-gray-900" {...props} />
                        ),
                        h2: ({ node, ...props }) => (
                          <h2 className="text-2xl font-semibold mt-8 mb-4 text-gray-900" {...props} />
                        ),
                        h3: ({ node, ...props }) => (
                          <h3 className="text-xl font-medium mt-6 mb-3 text-gray-800" {...props} />
                        ),
                        p: ({ node, ...props }) => (
                          <p className="text-lg text-gray-600 my-6 leading-relaxed" {...props} />
                        ),
                        ul: ({ node, ...props }) => <ul className="list-disc pl-8 my-6 space-y-4" {...props} />,
                        ol: ({ node, ...props }) => <ol className="list-decimal pl-8 my-6 space-y-4" {...props} />,
                        li: ({ node, ...props }) => (
                          <li className="text-lg text-gray-600 pl-2 leading-relaxed" {...props} />
                        ),
                        strong: ({ node, ...props }) => <strong className="font-semibold text-gray-800" {...props} />,
                        em: ({ node, ...props }) => <em className="italic text-gray-700" {...props} />,
                      }}
                    >
                      {item.fullText.split('\n').slice(1).join('\n')}
                    </ReactMarkdown>
                  </div>
                ) : null}
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-lg text-blue-600 hover:text-blue-800 mt-4"
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                </button>
              </div>
            )}
          </div>
        )

      case 'tech':
        return (
          <div className="mt-3">
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">{item.description}</p>
            <p className="mt-6 text-lg text-gray-600 dark:text-gray-300 leading-relaxed">{item.relevance}</p>
            {item.link && (
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center text-lg text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Learn more
                <ArrowRight size={16} className="ml-2" />
              </a>
            )}
          </div>
        )

      case 'market':
        return (
          <div className="mt-3">
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">{item.description}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              {item.companies.map((company, index) => (
                <Badge key={index} variant="outline" className="text-base px-3 py-1">
                  {company}
                </Badge>
              ))}
            </div>
            <p className="mt-6 text-lg text-gray-600 dark:text-gray-300 leading-relaxed">{item.impact}</p>
          </div>
        )

      case 'opportunity':
        return (
          <div className="mt-3">
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">{item.description}</p>
            <div className="mt-6 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-base text-gray-500 dark:text-gray-400">Data Points:</span>
                <span className="text-base font-medium dark:text-gray-300">{item.dataPoints}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-base text-gray-500 dark:text-gray-400">Impact:</span>
                <span className="text-base font-medium dark:text-gray-300">{item.percentage}%</span>
              </div>
              {item.priority && (
                <div className="flex items-center gap-2">
                  <span className="text-base text-gray-500 dark:text-gray-400">Priority:</span>
                  <Badge variant="outline" className="text-base px-3 py-1">
                    P{item.priority}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {getIcon()}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{(item as any).title}</h3>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-base text-gray-500 dark:text-gray-400">{getFormattedDate()}</span>
              {item.competitor && (
                <>
                  <span className="text-base text-gray-300 dark:text-gray-600">•</span>
                  <span className="text-base text-gray-500 dark:text-gray-400">{item.competitor}</span>
                </>
              )}
            </div>
          </div>
        </div>
        {(item as any).product && (
          <Badge variant="outline" className="text-base px-3 py-1">
            {(item as any).product}
          </Badge>
        )}
      </div>

      {/* Content */}
      {renderContent()}

      {/* Interactions */}
      {/* <div className="mt-8">
        <FeedInteractions
          reactions={reactions}
          comments={comments}
          onReactionAdd={handleReactionAdd}
          onCommentAdd={handleCommentAdd}
        />
      </div> */}
    </div>
  )
}

export default BaseFeedItem
